import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import MenuItem from "@mui/material/MenuItem"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import Typography from "@mui/material/Typography"
import fileDownload from "js-file-download"
import { useMemo, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import DialogHeader from "../components/DialogHeader"
import { useDialogContainer } from "../utils"
import { buildBulkListFilter } from "./agentListFilters"
import {
  BULK_SEARCH_FIELDS,
  BULK_SEARCH_MAX_ROWS,
  TABLE_PLACEHOLDER,
  buildTableRows,
  parseSingleColumn,
  parseTable,
  singleColumnPlaceholder,
  validateBulkRows,
} from "./bulkSearchParser"

const MODE_SINGLE = "single"
const MODE_TABLE = "table"
const FIELD_LABELS = Object.fromEntries(
  BULK_SEARCH_FIELDS.map((field) => [field.value, field.label])
)
const IGNORE_COLUMN = "__ignore__"
const PREVIEW_ROWS = 5

export const BULK_STATUS_LABELS = {
  matched: "Matched",
  multiple: "Several agents",
  ambiguous: "Needs review",
  not_found: "Not found",
  invalid: "Invalid value",
}
const STATUS_COLORS = {
  matched: "success",
  multiple: "info",
  ambiguous: "warning",
  not_found: "default",
  invalid: "error",
}

function describeInput(input) {
  return Object.entries(input)
    .map(([field, value]) => `${FIELD_LABELS[field] || field}: ${value}`)
    .join(" · ")
}

function describeCandidates(result) {
  if (!result.candidates.length) {
    return ""
  }
  const shown = result.candidates
    .slice(0, 3)
    .map((agent) =>
      [agent.name, agent.office_name].filter(Boolean).join(" at ")
    )
    .join("; ")
  const more = result.count - Math.min(result.candidates.length, 3)
  return more > 0 ? `${shown}; +${more} more` : shown
}

export function buildBulkSearchReportCsv(results) {
  const quote = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`
  const lines = [
    ["Row", "You searched", "Result", "Agents found", "Agents", "Note"]
      .map(quote)
      .join(","),
  ]
  results.forEach((result) => {
    lines.push(
      [
        result.index + 1,
        describeInput(result.input),
        BULK_STATUS_LABELS[result.status] || result.status,
        result.count,
        result.candidates
          .map((agent) =>
            [agent.name, agent.office_name, agent.city, agent.state]
              .filter(Boolean)
              .join(", ")
          )
          .join(" | "),
        result.note,
      ]
        .map(quote)
        .join(",")
    )
  })
  return lines.join("\n")
}

export default function BulkSearchDialog({
  open,
  onClose,
  onApply,
  onSaveAsView,
  savedViewPage,
}) {
  const [mode, setMode] = useState(MODE_SINGLE)
  const [singleField, setSingleField] = useState("name")
  const [text, setText] = useState("")
  const [columnOverrides, setColumnOverrides] = useState({})
  const [outcome, setOutcome] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const [viewName, setViewName] = useState("")
  const [isSavingView, setIsSavingView] = useState(false)
  const [errorText, setErrorText] = useState(null)

  const isMobile = useIsMobile()
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  const single = useMemo(
    () => parseSingleColumn(text, singleField),
    [text, singleField]
  )
  const table = useMemo(() => parseTable(text), [text])
  const columns = table.columns.map((column) =>
    column.index in columnOverrides
      ? { ...column, field: columnOverrides[column.index] }
      : column
  )
  const rows =
    mode === MODE_SINGLE ? single.rows : buildTableRows(columns, table.body)
  const validationError = text.trim() ? validateBulkRows(rows) : null
  const canSearch = rows.length > 0 && !validationError && !isSearching

  function handleTextChanged(e) {
    setText(e.target.value)
    setColumnOverrides({})
    setErrorText(null)
  }

  function handleModeChanged(_, newMode) {
    if (newMode) {
      setMode(newMode)
      setColumnOverrides({})
      setErrorText(null)
    }
  }

  function handleColumnChanged(index, value) {
    setColumnOverrides((prev) => ({
      ...prev,
      [index]: value === IGNORE_COLUMN ? null : value,
    }))
  }

  function handleSoftClose() {
    if (!isSearching && !isSavingView) {
      onClose()
    }
  }

  function handleSearchClicked() {
    setIsSearching(true)
    setErrorText(null)
    axios
      .post("/crm/api/agents/bulk-match/", {
        rows,
        name: `Bulk search of ${rows.length} rows`,
      })
      .then(({ data }) => {
        setOutcome(data)
        setViewName(`Bulk search ${new Date().toLocaleDateString()}`)
      })
      .catch((error) => {
        const detail = error.response?.data?.rows
        setErrorText(
          (Array.isArray(detail) ? detail[0] : detail) ||
            "The search failed. Please try again."
        )
      })
      .finally(() => setIsSearching(false))
  }

  function handleApplyClicked() {
    onApply(outcome)
    handleReset()
    onClose()
  }

  // A saved view keeps the search after a refresh: pin the list so it is never cleaned
  // up, then create the view with the bulk_list filter in it.
  function handleSaveAsViewClicked() {
    const name = viewName.trim()
    setIsSavingView(true)
    setErrorText(null)
    axios
      .patch(`/crm/api/agents/bulk-match/${outcome.list_id}/`, {
        pinned: true,
        name,
      })
      .then(() =>
        axios.post("/crm/api/saved-filter/", {
          name,
          page: savedViewPage,
          filters: [{ ...buildBulkListFilter(outcome), _complete: true }],
        })
      )
      .then(({ data }) => {
        onSaveAsView(data)
        handleReset()
        onClose()
      })
      .catch(() =>
        setErrorText("The view could not be saved. Please try again.")
      )
      .finally(() => setIsSavingView(false))
  }

  // Back to the paste box with everything the user entered (mode, field, text and
  // column mapping) kept, to change it and search again.
  function handleBackClicked() {
    setOutcome(null)
    setErrorText(null)
  }

  function handleReset() {
    setOutcome(null)
    setText("")
    setColumnOverrides({})
    setErrorText(null)
  }

  function handleDownloadClicked() {
    fileDownload(
      buildBulkSearchReportCsv(outcome.results),
      "bulk-search-report.csv",
      "text/csv"
    )
  }

  const summary = outcome?.summary

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      fullScreen={isMobile}
      PaperProps={{ sx: { width: { sm: 760 }, maxWidth: "100%" } }}
    >
      <DialogHeader title="Bulk search" onClose={handleSoftClose} />
      <DialogContent>
        {outcome ? (
          <Column sx={{ rowGap: 2, mt: 1 }}>
            <Button
              variant="text"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBackClicked}
              disabled={isSavingView}
              sx={{ alignSelf: "flex-start" }}
            >
              Back to my search
            </Button>
            <Row sx={{ flexWrap: "wrap", gap: 1 }}>
              <Chip label={`${summary.rows} rows`} />
              <Chip
                color="success"
                label={`${summary.matched} matched`}
                variant="outlined"
              />
              {summary.multiple > 0 && (
                <Chip
                  color="info"
                  label={`${summary.multiple} with several agents`}
                  variant="outlined"
                />
              )}
              {summary.ambiguous > 0 && (
                <Chip
                  color="warning"
                  label={`${summary.ambiguous} need review`}
                  variant="outlined"
                />
              )}
              <Chip
                label={`${summary.not_found} not found`}
                variant="outlined"
              />
              {summary.invalid > 0 && (
                <Chip
                  color="error"
                  label={`${summary.invalid} invalid`}
                  variant="outlined"
                />
              )}
            </Row>
            <TextField
              size="small"
              label="View name"
              value={viewName}
              onChange={(e) => setViewName(e.target.value.slice(0, 128))}
              helperText="Save the result as a view to keep it. A view stays after you refresh the page."
            />
            <Typography variant="body2" color="text.secondary">
              {summary.agents} agents will be shown in My Leads. Rows marked{" "}
              <strong>Needs review</strong> matched more than one agent with the
              same name, so all of them are shown. Add a company, city or email
              column to tell them apart.
            </Typography>
            {summary.truncated && (
              <Alert severity="warning">
                This search matched more agents than one list can hold, so only
                the first {summary.agents} are shown. Add more columns to narrow
                it.
              </Alert>
            )}
            <TableContainer sx={{ maxHeight: "55vh" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>You searched</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Found</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outcome.results.map((result) => (
                    <TableRow key={result.index}>
                      <TableCell>{result.index + 1}</TableCell>
                      <TableCell>{describeInput(result.input)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={STATUS_COLORS[result.status]}
                          label={BULK_STATUS_LABELS[result.status]}
                        />
                      </TableCell>
                      <TableCell>
                        {describeCandidates(result) || result.note}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Column>
        ) : (
          <Column sx={{ rowGap: 2, mt: 1 }}>
            <Alert severity="info">
              Search up to <strong>{BULK_SEARCH_MAX_ROWS} rows</strong> at once.
              Paste names, emails, phone numbers, companies, addresses, cities,
              states, ZIP codes or MLS member ids. You see which rows were found
              and which were not.
            </Alert>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={handleModeChanged}
            >
              <ToggleButton value={MODE_SINGLE}>One column</ToggleButton>
              <ToggleButton value={MODE_TABLE}>Several columns</ToggleButton>
            </ToggleButtonGroup>
            {mode === MODE_SINGLE ? (
              <>
                <TextField
                  select
                  size="small"
                  label="Search by"
                  value={singleField}
                  onChange={(e) => setSingleField(e.target.value)}
                >
                  {BULK_SEARCH_FIELDS.filter(
                    (field) => field.value !== "mls"
                  ).map((field) => (
                    <MenuItem key={field.value} value={field.value}>
                      {field.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" color="text.secondary">
                  Put <strong>one value on each line</strong>. A semicolon also
                  separates values. For email, phone, state, ZIP and member id,
                  commas work too. Names and addresses can contain commas, so
                  only new lines and semicolons split them.
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Copy the cells from Excel or Google Sheets and paste them here.
                If you type it by hand, separate the columns with{" "}
                <strong>|</strong> (a pipe) and put each row on a new line. The
                first line may be a header such as{" "}
                <em>Name | Email | Company</em>. Commas separate columns only
                when the first line is a header.
              </Typography>
            )}
            <TextField
              multiline
              minRows={8}
              maxRows={16}
              value={text}
              onChange={handleTextChanged}
              inputProps={{ "aria-label": "Values to search" }}
              placeholder={
                mode === MODE_SINGLE
                  ? singleColumnPlaceholder(singleField)
                  : TABLE_PLACEHOLDER
              }
            />
            <Row sx={{ justifyContent: "space-between" }}>
              <Typography
                variant="body2"
                color={validationError ? "error" : "text.secondary"}
              >
                {validationError ||
                  `${rows.length} row${rows.length === 1 ? "" : "s"} ready`}
                {mode === MODE_SINGLE && single.duplicates > 0
                  ? ` (${single.duplicates} duplicate${
                      single.duplicates === 1 ? "" : "s"
                    } removed)`
                  : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maximum {BULK_SEARCH_MAX_ROWS} rows
              </Typography>
            </Row>
            {mode === MODE_TABLE && table.columns.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell key={column.index} sx={{ minWidth: 150 }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            label={
                              column.header || `Column ${column.index + 1}`
                            }
                            value={column.field || IGNORE_COLUMN}
                            onChange={(e) =>
                              handleColumnChanged(column.index, e.target.value)
                            }
                          >
                            <MenuItem value={IGNORE_COLUMN}>
                              Ignore this column
                            </MenuItem>
                            {BULK_SEARCH_FIELDS.map((field) => (
                              <MenuItem key={field.value} value={field.value}>
                                {field.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {table.body.slice(0, PREVIEW_ROWS).map((cells, i) => (
                      <TableRow key={i}>
                        {columns.map((column) => (
                          <TableCell key={column.index}>
                            {cells[column.index] || ""}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {errorText && <Alert severity="error">{errorText}</Alert>}
          </Column>
        )}
        {outcome && errorText ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorText}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        {outcome ? (
          <>
            <Button variant="outlined" onClick={handleDownloadClicked}>
              Download report
            </Button>
            <Button
              variant="outlined"
              onClick={handleApplyClicked}
              disabled={!summary.agents || isSavingView}
            >
              Show without saving
            </Button>
            <LoadingButton
              variant="contained"
              loading={isSavingView}
              onClick={handleSaveAsViewClicked}
              disabled={!summary.agents || !viewName.trim()}
            >
              Save as view and show {summary.agents} agents
            </LoadingButton>
          </>
        ) : (
          <>
            <Button variant="outlined" onClick={handleSoftClose}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              loading={isSearching}
              disabled={!canSearch}
              onClick={handleSearchClicked}
            >
              Search {rows.length > 0 ? `${rows.length} rows` : ""}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Container>
  )
}
